import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export default function GirisPage() {
  const [mode, setMode] = useState<'giris' | 'kayit'>('giris');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || '/';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (mode === 'kayit') {
      if (!form.firstName.trim() || !form.lastName.trim()) {
        setError('Ad ve soyad zorunludur.');
        return;
      }
      if (form.password.length < 6) {
        setError('Şifre en az 6 karakter olmalıdır.');
        return;
      }
      if (form.password !== form.confirmPassword) {
        setError('Şifreler eşleşmiyor.');
        return;
      }
      setLoading(true);
      const res = await register(form.firstName, form.lastName, form.email, form.password);
      setLoading(false);
      if (!res.success) { setError(res.error || 'Kayıt başarısız.'); return; }
      navigate(from, { replace: true });
    } else {
      setLoading(true);
      const res = await login(form.email, form.password);
      setLoading(false);
      if (!res.success) { setError(res.error || 'Giriş başarısız.'); return; }
      navigate(from, { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left decorative panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/0f6320421152eaffa3ceba56e116d018.jpg"
          alt="Türkiye manzarası"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-900/70 via-orange-800/50 to-black/60" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/">
            <img
              src="https://storage.readdy-site.link/project_files/904466f0-2c05-4555-9f8d-869e0816a927/e2d94e9e-889c-4573-8429-0052ad575f89_Adsz-tasarm-3.png?v=589ebfa6144df8359c3a9f9f79357ea7"
              alt="Ankara Travelers"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <div>
            <h2 className="text-4xl font-bold text-white leading-tight mb-4">
              Hayalinizdeki<br />Tura Hazır mısınız?
            </h2>
            <p className="text-orange-100 text-base leading-relaxed mb-8">
              Hesabınızla rezervasyonlarınızı takip edin, özel fırsatlardan ilk siz haberdar olun.
            </p>
            <div className="flex flex-col gap-3">
              {[
                { icon: 'ri-calendar-check-line', text: 'Rezervasyonlarınızı kolayca yönetin' },
                { icon: 'ri-gift-line', text: 'Üyelere özel indirim ve kampanyalar' },
                { icon: 'ri-customer-service-2-line', text: '7/24 öncelikli müşteri desteği' },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-lg">
                    <i className={`${item.icon} text-white text-sm`} />
                  </div>
                  <span className="text-white/90 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <Link to="/">
              <img
                src="https://storage.readdy-site.link/project_files/904466f0-2c05-4555-9f8d-869e0816a927/e2d94e9e-889c-4573-8429-0052ad575f89_Adsz-tasarm-3.png?v=589ebfa6144df8359c3a9f9f79357ea7"
                alt="Ankara Travelers"
                className="h-10 w-auto object-contain mx-auto"
              />
            </Link>
          </div>

          {/* Tab switcher */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-8">
            <button
              onClick={() => { setMode('giris'); setError(''); }}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                mode === 'giris' ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Giriş Yap
            </button>
            <button
              onClick={() => { setMode('kayit'); setError(''); }}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                mode === 'kayit' ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Hesap Oluştur
            </button>
          </div>

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === 'giris' ? 'Tekrar hoş geldiniz!' : 'Hesap oluşturun'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {mode === 'giris'
                ? 'Devam etmek için giriş yapın'
                : 'Ücretsiz hesabınızı oluşturun'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'kayit' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Ad *</label>
                  <input
                    name="firstName"
                    type="text"
                    required
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder="Adınız"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Soyad *</label>
                  <input
                    name="lastName"
                    type="text"
                    required
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder="Soyadınız"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">E-posta *</label>
              <input
                name="email"
                type="email"
                required
                value={form.email}
                onChange={handleChange}
                placeholder="ornek@mail.com"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Şifre *</label>
              <input
                name="password"
                type="password"
                required
                value={form.password}
                onChange={handleChange}
                placeholder={mode === 'kayit' ? 'En az 6 karakter' : 'Şifreniz'}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
              />
            </div>

            {mode === 'kayit' && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Şifre Tekrar *</label>
                <input
                  name="confirmPassword"
                  type="password"
                  required
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="Şifrenizi tekrar girin"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-orange-400 transition-colors"
                />
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <i className="ri-error-warning-line text-red-500 text-sm shrink-0" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white py-3.5 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 mt-2"
            >
              {loading ? (
                <><i className="ri-loader-4-line animate-spin" /><span>İşleniyor...</span></>
              ) : mode === 'giris' ? (
                <><i className="ri-login-box-line" /><span>Giriş Yap</span></>
              ) : (
                <><i className="ri-user-add-line" /><span>Hesap Oluştur</span></>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-6">
            {mode === 'giris' ? 'Hesabınız yok mu?' : 'Zaten hesabınız var mı?'}{' '}
            <button
              onClick={() => { setMode(mode === 'giris' ? 'kayit' : 'giris'); setError(''); }}
              className="text-orange-500 hover:text-orange-600 font-medium cursor-pointer"
            >
              {mode === 'giris' ? 'Kayıt olun' : 'Giriş yapın'}
            </button>
          </p>

          <div className="mt-6 text-center">
            <Link to="/" className="text-xs text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
              ← Ana Sayfaya Dön
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
